import React, { Component, createRef } from 'react'
import classnames from 'classnames'

export function resizeImage(
  degrees: number,
  imgCanvas: HTMLCanvasElement,
  image: HTMLImageElement
) {
  if (degrees === 90 || degrees === 270) {
    imgCanvas.width = image.height
    imgCanvas.height = image.width
  } else {
    imgCanvas.width = image.width
    imgCanvas.height = image.height
  }
}

export function draftCanvas(
  degrees: number,
  imgCanvas: HTMLCanvasElement,
  image: HTMLImageElement
): void {
  const context = imgCanvas.getContext('2d')
  if (!context) return

  context.clearRect(0, 0, imgCanvas.width, imgCanvas.height)
  if (degrees === 90 || degrees === 270) {
    context.translate(image.height / 2, image.width / 2)
  } else {
    context.translate(image.width / 2, image.height / 2)
  }
  context.rotate((degrees * Math.PI) / 180)
  context.drawImage(image, -image.width / 2, -image.height / 2)
}

export function _updateImage(
  _resizeImage: typeof resizeImage,
  _draftCanvas: typeof draftCanvas
) {
  return (degrees = 0) => {
    return (imgCanvas: HTMLCanvasElement, image: HTMLImageElement) => {
      _resizeImage(degrees, imgCanvas, image)
      _draftCanvas(degrees, imgCanvas, image)

      return { rotation: degrees }
    }
  }
}

const updateImage = _updateImage(resizeImage, draftCanvas)

export function rotateLeft(rotation: number) {
  if (rotation === 0) {
    return 270
  } else {
    return (rotation - 90) % 360
  }
}

export function rotateRight(rotation: number) {
  return (rotation + 90) % 360
}

export type Callback<T = void> = (...args: any) => T

export interface Props {
  closeModal?: Callback
  imgId?: string
  imgUrl: string
  saveImg?: Callback
  toggleLayout?: Callback
}

export class TaskImage extends Component<Props> {
  static displayName = 'TaskImg'

  state = { rotation: 0 }

  componentWillMount() {
    window.addEventListener('keydown', this.handleKeyDown.bind(this))
  }

  componentDidMount() {
    window.removeEventListener('keydown', this.handleKeyDown.bind(this))
  }

  private imgCanvas = createRef<HTMLCanvasElement>()
  private image = createRef<HTMLImageElement>()

  handleKeyDown = (event) => {
    if (event.keyCode === 39) {
      this.clickRight()
    } else if (event.keyCode === 37) {
      this.clickLeft()
    }
  }

  clickRight = () => {
    const canvas = this.imgCanvas.current
    if (!canvas) return

    const image = this.image.current
    if (!image) return

    this.setState(updateImage(rotateRight(this.state.rotation))(canvas, image))
  }

  clickLeft = () => {
    const canvas = this.imgCanvas.current
    if (!canvas) return

    const image = this.image.current
    if (!image) return

    this.setState(updateImage(rotateLeft(this.state.rotation))(canvas, image))
  }

  onLoad = () => {
    const canvas = this.imgCanvas.current
    if (!canvas) return

    const image = this.image.current
    if (!image) return

    this.setState(updateImage(0)(canvas, image))
  }

  onSave = () => {
    const { closeModal, saveImg, imgId } = this.props
    saveImg && saveImg(imgId, this.state.rotation)
    closeModal && closeModal()
  }

  render() {
    const { imgUrl, saveImg, toggleLayout } = this.props

    return (
      <div className="task-image">
        <canvas
          ref={this.imgCanvas}
          className={classnames('task-image__canvas', {
            'task-image__canvas--clickable': toggleLayout,
          })}
          data-testid="task-image-canvas"
          onClick={toggleLayout}
        >
          <img src={imgUrl} ref={this.image} onLoad={this.onLoad} />
        </canvas>
        <a
          onClick={this.clickLeft}
          data-testid="rotateImageLeftLink"
          className="task-image__button task-image__button--left icon-rotate-l"
        />
        {saveImg && (
          <button
            data-testid="task-image-save-button"
            onClick={this.onSave}
            className="button button--primary-round button--full-width"
          >
            Save
          </button>
        )}
        <a
          onClick={this.clickRight}
          data-testid="rotateImageRightLink"
          className="task-image__button task-image__button--right icon-rotate-r"
        />
      </div>
    )
  }
}

export default TaskImage
